import { useCallback, useEffect, useState } from 'react'
import { Loader2, UserPlus } from 'lucide-react'

import { SalonPageShell } from '@/components/layout/SalonPageShell'
import { Button } from '@/components/ui/button'
import { api } from '@/lib/api'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'

const STATUSES = [
  { id: 'new', label: 'New' },
  { id: 'contacted', label: 'Contacted' },
  { id: 'booked', label: 'Booked' },
  { id: 'lost', label: 'Lost' },
]

const inputClass = cn(
  'h-10 w-full rounded-xl border border-border/80 bg-card px-3 text-sm',
  'focus:outline-none focus:ring-2 focus:ring-ring',
)

export function LeadsPage() {
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [leads, setLeads] = useState([])
  const [statusFilter, setStatusFilter] = useState('')
  const [form, setForm] = useState({
    name: '',
    phone: '',
    email: '',
    source: '',
    notes: '',
  })

  const refresh = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const params = statusFilter ? { status: statusFilter } : {}
      const { data } = await api.get('/crm/leads', { params })
      setLeads(data?.data?.leads ?? [])
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to load leads.'))
      setLeads([])
    } finally {
      setLoading(false)
    }
  }, [statusFilter])

  useEffect(() => {
    void refresh()
  }, [refresh])

  async function handleCreate(event) {
    event.preventDefault()
    setBusy(true)
    setError('')
    try {
      await api.post('/crm/leads', {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        source: form.source || null,
        notes: form.notes || null,
      })
      setForm({ name: '', phone: '', email: '', source: '', notes: '' })
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to create lead.'))
    } finally {
      setBusy(false)
    }
  }

  async function handleStatus(leadId, status) {
    setBusy(true)
    setError('')
    try {
      await api.put(`/crm/leads/${leadId}`, { status })
      await refresh()
    } catch (err) {
      setError(apiErrorMessage(err, 'Unable to update lead.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <SalonPageShell
      title="Leads"
      description="Track new enquiries through New → Contacted → Booked → Lost."
    >
      <div className="mb-4 flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant={statusFilter === '' ? 'default' : 'outline'}
          onClick={() => setStatusFilter('')}
        >
          All
        </Button>
        {STATUSES.map((status) => (
          <Button
            key={status.id}
            type="button"
            size="sm"
            variant={statusFilter === status.id ? 'default' : 'outline'}
            onClick={() => setStatusFilter(status.id)}
          >
            {status.label}
          </Button>
        ))}
      </div>

      {error ? (
        <div className="mb-4 rounded-xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          {error}
        </div>
      ) : null}

      <form
        onSubmit={handleCreate}
        className="mb-6 grid max-w-3xl gap-3 rounded-2xl border border-border/70 bg-card p-4 sm:grid-cols-2"
      >
        <h2 className="sm:col-span-2 flex items-center gap-2 text-lg font-medium">
          <UserPlus className="h-5 w-5 text-primary" />
          New lead
        </h2>
        <input
          className={inputClass}
          required
          placeholder="Name"
          value={form.name}
          onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
        />
        <input
          className={inputClass}
          required
          placeholder="Phone"
          value={form.phone}
          onChange={(e) => setForm((current) => ({ ...current, phone: e.target.value }))}
        />
        <input
          className={inputClass}
          type="email"
          placeholder="Email (optional)"
          value={form.email}
          onChange={(e) => setForm((current) => ({ ...current, email: e.target.value }))}
        />
        <input
          className={inputClass}
          placeholder="Source (optional)"
          value={form.source}
          onChange={(e) => setForm((current) => ({ ...current, source: e.target.value }))}
        />
        <textarea
          className={cn(inputClass, 'h-auto min-h-20 py-2 sm:col-span-2')}
          placeholder="Notes"
          value={form.notes}
          onChange={(e) => setForm((current) => ({ ...current, notes: e.target.value }))}
        />
        <Button type="submit" disabled={busy} className="w-fit">
          Save lead
        </Button>
      </form>

      {loading ? (
        <p className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </p>
      ) : (
        <ul className="max-w-3xl divide-y divide-border/60 rounded-2xl border border-border/70 bg-card">
          {leads.length === 0 ? (
            <li className="p-4 text-sm text-muted-foreground">No leads yet.</li>
          ) : (
            leads.map((lead) => (
              <li key={lead.id} className="space-y-2 px-4 py-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <p className="font-medium">{lead.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {lead.phone}
                      {lead.email ? ` · ${lead.email}` : ''}
                      {lead.source ? ` · ${lead.source}` : ''}
                    </p>
                    {lead.notes ? <p className="mt-1 text-sm text-muted-foreground">{lead.notes}</p> : null}
                  </div>
                  <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium capitalize">
                    {lead.status}
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {STATUSES.map((status) => (
                    <Button
                      key={status.id}
                      type="button"
                      size="sm"
                      variant={lead.status === status.id ? 'default' : 'outline'}
                      disabled={busy || lead.status === status.id}
                      onClick={() => handleStatus(lead.id, status.id)}
                    >
                      {status.label}
                    </Button>
                  ))}
                </div>
              </li>
            ))
          )}
        </ul>
      )}
    </SalonPageShell>
  )
}
